import React, { useState } from 'react';
import { Form, Input, Select } from 'antd';
import type { NpiLookupTaxonomy } from 'types/insuranceBilling';
import { TAXONOMY_CODE_RE } from '../../utils';
import s from './s.module.less';

const WarnIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" fill="currentColor" />
        <path d="M12 10v3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="16.8" r="1.1" fill="#fff" />
    </svg>
);

// 下拉里「不在列表中」选项的占位值（不会存进表单）
const OTHER = '__other__';

type Props = {
    // 由 Form.Item 注入：taxonomy code
    value?: string;
    onChange?: (v: string | undefined) => void;
    options: NpiLookupTaxonomy[];
    // 手填标记所在路径（[...ns, 'taxManual']）
    manualPath: (string | number)[];
};

// 原型 V3 Taxonomy：NPPES 记录过期的 provider 可选末项 My taxonomy is not listed here 手填 code。
// code 仍存 taxonomy 字段，taxManual 标记它是手填的（Review / Claim settings 标 Differs from NPPES）
export default function TaxonomyPicker({ value, onChange, options, manualPath }: Props) {
    const form = Form.useFormInstance();
    const manual = !!Form.useWatch(manualPath, form);
    // 失焦或输满 10 位后才报格式错误，避免边输边红
    const [touched, setTouched] = useState(false);

    const handleSelect = (v: string) => {
        if (v === OTHER) {
            form.setFieldValue(manualPath, true);
            setTouched(false);
            onChange?.('');
            return;
        }
        form.setFieldValue(manualPath, false);
        onChange?.(v);
    };

    const code = value || '';
    let error = '';
    if (manual && (touched || code.length >= 10)) {
        if (!code) error = 'Enter your taxonomy code.';
        else if (!TAXONOMY_CODE_RE.test(code))
            error = 'This is not a valid taxonomy code. It has 10 characters and ends in X, like 363LP0808X.';
    }

    return (
        <>
            <Select
                size="large"
                value={manual ? OTHER : value}
                onChange={handleSelect}
                options={[
                    ...options.map((t) => ({
                        value: t.code,
                        label: `${t.code} · ${t.desc}${t.primary ? ' (primary)' : ''}`,
                    })),
                    { value: OTHER, label: 'My taxonomy is not listed here' },
                ]}
            />
            {manual && (
                <div className={s.taxManual}>
                    <div className={s.taxManualLabel}>Taxonomy code</div>
                    <Input
                        size="large"
                        placeholder="e.g. 363LP0808X"
                        value={code}
                        status={error ? 'error' : undefined}
                        // 不用原生 maxLength：它先截前 10 个字符，粘贴「  363LP0808X  」会被截成「363LP080」；先清洗再截
                        onChange={(e) =>
                            onChange?.(
                                e.target.value
                                    .toUpperCase()
                                    .replace(/[^0-9A-Z]/g, '')
                                    .slice(0, 10),
                            )
                        }
                        onBlur={() => setTouched(true)}
                    />
                    {error && <div className={s.taxManualError}>{error}</div>}
                    <div className={s.taxManualWarn}>
                        <WarnIcon />
                        <span>
                            Claims are billed under this code, and a wrong code gets them denied. Enter the exact
                            10-character code from the{' '}
                            <a href="https://taxonomy.nucc.org/" target="_blank" rel="noopener noreferrer">
                                NUCC taxonomy list
                            </a>
                            , and update your NPPES record when you can.
                        </span>
                    </div>
                </div>
            )}
        </>
    );
}
